<template>
  <div class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
    <div class="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
      <div class="mt-3">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-gray-900">
            {{ domain ? 'Edit Domain' : 'Add New Domain' }}
          </h3>
          <button
            @click="$emit('close')"
            class="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>
        
        <form @submit.prevent="saveDomain" class="space-y-4">
          <div>
            <label class="form-label">Domain Name</label>
            <div class="flex space-x-2">
              <input
                v-model="form.domainName"
                type="text"
                class="input-field flex-1"
                placeholder="example.com"
                required
                :disabled="!!domain"
              />
              <button
                v-if="!domain"
                type="button"
                @click="validateDomain"
                :disabled="!form.domainName || validating"
                class="btn-secondary px-4"
              >
                <span v-if="validating" class="flex items-center">
                  <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                  Validating...
                </span>
                <span v-else>Validate</span>
              </button>
            </div>
            <div v-if="validationMessage" class="mt-1 text-sm" :class="validationMessage.type === 'error' ? 'text-red-600' : 'text-green-600'">
              {{ validationMessage.text }}
            </div>
          </div>
          
          <div v-if="domain">
            <label class="form-label">Current Score</label>
            <div class="flex items-center space-x-2">
              <div class="text-2xl font-bold" :class="getScoreColorClass(domain.CalculatedScore)">
                {{ domain.CalculatedScore }}
              </div>
              <span class="text-sm text-gray-500">/ 100</span>
              <button
                type="button"
                @click="refreshScore"
                :disabled="refreshing"
                class="btn-secondary px-3 py-1 text-sm"
              >
                <span v-if="refreshing" class="flex items-center">
                  <div class="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600 mr-1"></div>
                  Refreshing...
                </span>
                <span v-else>Refresh</span>
              </button>
            </div>
          </div>
          
          <div>
            <label class="form-label">Description (Optional)</label>
            <textarea
              v-model="form.description"
              class="input-field"
              rows="3"
              placeholder="Optional description or notes about this domain"
            ></textarea>
          </div>
          
          <!-- Tracking Settings (only when editing) -->
          <div v-if="domain" class="space-y-4 border-t pt-4">
            <h4 class="text-md font-semibold text-gray-700">Tracking Settings</h4>
            
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <label class="form-label mb-1">Open Tracking</label>
                <p class="text-sm text-gray-500">Track when emails are opened</p>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  v-model="form.openTracking"
                  @change="updateTracking"
                  :disabled="updatingTracking"
                  class="sr-only peer"
                />
                <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>
            
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <label class="form-label mb-1">Click Tracking</label>
                <p class="text-sm text-gray-500">Track when links in emails are clicked</p>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  v-model="form.clickTracking"
                  @change="updateTracking"
                  :disabled="updatingTracking"
                  class="sr-only peer"
                />
                <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>
          </div>
          
          <div v-if="!domain">
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div class="flex">
                <div class="flex-shrink-0">
                  <InformationCircleIcon class="h-5 w-5 text-blue-400" />
                </div>
                <div class="ml-3">
                  <h3 class="text-sm font-medium text-blue-800">Domain Setup</h3>
                  <div class="mt-2 text-sm text-blue-700">
                    <p>Before using this domain for sending emails, you'll need to:</p>
                    <ul class="list-disc list-inside mt-1 space-y-1">
                      <li>Configure DNS records (SPF, DKIM, DMARC)</li>
                      <li>Verify domain ownership with Mailgun</li>
                      <li>Wait for DNS propagation (can take up to 48 hours)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              @click="$emit('close')"
              class="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="saving || (!domain && !validationMessage?.isValid)"
              class="btn-primary"
            >
              <span v-if="saving" class="flex items-center">
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </span>
              <span v-else>{{ domain ? 'Update' : 'Add' }} Domain</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { domainsApi } from '../services/api'
import { toast } from 'vue3-toastify'
import { XMarkIcon, InformationCircleIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  domain: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['close', 'saved'])

// Form state
const form = ref({
  domainName: '',
  description: '',
  openTracking: false,
  clickTracking: false
})

const saving = ref(false)
const validating = ref(false)
const refreshing = ref(false)
const updatingTracking = ref(false)
const validationMessage = ref(null)
const originalTracking = ref({ openTracking: false, clickTracking: false })

// Watch for domain changes to populate form
watch(() => props.domain, async (newDomain) => {
  if (newDomain) {
    // Fetch fresh domain data to get current tracking settings
    try {
      const response = await domainsApi.getById(newDomain.DomainID)
      if (response.data.success) {
        const domainData = response.data.data
        form.value = {
          domainName: domainData.DomainName || '',
          description: domainData.Description || '',
          openTracking: domainData.OpenTracking || domainData.openTracking || false,
          clickTracking: domainData.ClickTracking || domainData.clickTracking || false
        }
        // Store original tracking values to detect changes
        originalTracking.value = {
          openTracking: domainData.OpenTracking || domainData.openTracking || false,
          clickTracking: domainData.ClickTracking || domainData.clickTracking || false
        }
      } else {
        // Fallback to prop values if fetch fails
        form.value = {
          domainName: newDomain.DomainName || '',
          description: newDomain.Description || '',
          openTracking: newDomain.OpenTracking || false,
          clickTracking: newDomain.ClickTracking || false
        }
        originalTracking.value = {
          openTracking: newDomain.OpenTracking || false,
          clickTracking: newDomain.ClickTracking || false
        }
      }
    } catch (error) {
      console.error('Error fetching domain details:', error)
      // Fallback to prop values
      form.value = {
        domainName: newDomain.DomainName || '',
        description: newDomain.Description || '',
        openTracking: newDomain.OpenTracking || false,
        clickTracking: newDomain.ClickTracking || false
      }
      originalTracking.value = {
        openTracking: newDomain.OpenTracking || false,
        clickTracking: newDomain.ClickTracking || false
      }
    }
  } else {
    // Reset form for new domain
    form.value = {
      domainName: '',
      description: '',
      openTracking: false,
      clickTracking: false
    }
    originalTracking.value = { openTracking: false, clickTracking: false }
    validationMessage.value = null
  }
}, { immediate: true })

// Methods
const validateDomain = async () => {
  if (!form.value.domainName) return
  
  try {
    validating.value = true
    validationMessage.value = null
    
    // This would call a domain validation endpoint
    // For now, we'll simulate validation
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    // Simulate validation result
    const isValid = form.value.domainName.includes('.') && form.value.domainName.length > 3
    validationMessage.value = {
      text: isValid ? 'Domain format is valid' : 'Invalid domain format',
      type: isValid ? 'success' : 'error',
      isValid
    }
    
    if (isValid) {
      toast.success('Domain validation successful')
    } else {
      toast.error('Domain validation failed')
    }
  } catch (error) {
    console.error('Error validating domain:', error)
    validationMessage.value = {
      text: 'Error validating domain',
      type: 'error',
      isValid: false
    }
    toast.error('Failed to validate domain')
  } finally {
    validating.value = false
  }
}

const refreshScore = async () => {
  if (!props.domain) return
  
  try {
    refreshing.value = true
    
    // This would call the refresh score endpoint
    await domainsApi.refreshScore(props.domain.DomainID)
    
    toast.success('Domain score refreshed successfully')
    emit('saved') // Trigger a refresh of the parent component
  } catch (error) {
    console.error('Error refreshing score:', error)
    toast.error('Failed to refresh domain score')
  } finally {
    refreshing.value = false
  }
}

const getScoreColorClass = (score) => {
  if (score >= 80) return 'text-green-600'
  if (score >= 60) return 'text-yellow-600'
  return 'text-red-600'
}

const updateTracking = async () => {
  if (!props.domain) return
  
  // Check if tracking values actually changed
  const hasChanged = 
    form.value.openTracking !== originalTracking.value.openTracking ||
    form.value.clickTracking !== originalTracking.value.clickTracking
  
  if (!hasChanged) return
  
  try {
    updatingTracking.value = true
    
    await domainsApi.updateTracking(props.domain.DomainID, {
      openTracking: form.value.openTracking,
      clickTracking: form.value.clickTracking
    })
    
    // Update original tracking values
    originalTracking.value = {
      openTracking: form.value.openTracking,
      clickTracking: form.value.clickTracking
    }
    
    toast.success('Tracking settings updated successfully')
  } catch (error) {
    console.error('Error updating tracking:', error)
    // Revert to original values on error
    form.value.openTracking = originalTracking.value.openTracking
    form.value.clickTracking = originalTracking.value.clickTracking
    toast.error('Failed to update tracking settings')
  } finally {
    updatingTracking.value = false
  }
}

const saveDomain = async () => {
  try {
    saving.value = true
    
    if (props.domain) {
      // Update existing domain
      await domainsApi.update(props.domain.DomainID, {
        description: form.value.description
      })
      toast.success('Domain updated successfully')
    } else {
      // Add new domain
      await domainsApi.create({
        domainName: form.value.domainName,
        description: form.value.description
      })
      toast.success('Domain added successfully')
    }
    
    emit('saved')
  } catch (error) {
    console.error('Error saving domain:', error)
    toast.error('Failed to save domain')
  } finally {
    saving.value = false
  }
}

// Lifecycle
onMounted(() => {
  // Form is already initialized by the watcher
})
</script> 